import { PurchaseType } from '@/enums/purchase-type';

export interface PaymentTypeConfig {
  key:
    | 'cashSales'
    | 'walletSales'
    | 'campaignSales'
    | 'cardSales'
    | 'directSales'
    | 'physicalCardSales'
    | 'mealCardSales'
    | 'externalPlatformSales'
    | 'openAccountSales'
    | 'loyaltyPointsSales'
    | 'loyaltyHybridSales'
    | 'membershipPurchaseSales'
    | 'mixedSales'
    | 'unknownSales';
  label: string;
  // Overrides `label` in the sales-by-type breakdown only. `mixedSales` there
  // is just the unattributed part of MIXED sales, while `label` still names the
  // full MIXED tender for filters/charts.
  statsLabel?: string;
  color: string;
  enumValue?: PurchaseType;
  // `isReceivable=true` marks a fiscal tender that prints a receipt but takes
  // no money in (Açık Hesap — customer owes the branch). Rendered with a
  // distinct "not collected" style so operators don't read it as cash on hand.
  isReceivable?: boolean;
  // `isDataQuality=true` marks a bucket that indicates a bug (null purchaseType).
  isDataQuality?: boolean;
}

export const PAYMENT_TYPES: PaymentTypeConfig[] = [
  {
    key: 'cashSales',
    label: 'dashboard.cash.sales',
    color: '#4CAF50',
    enumValue: PurchaseType.CASH
  },
  {
    key: 'walletSales',
    label: 'dashboard.wallet.sales',
    color: '#2196F3',
    enumValue: PurchaseType.WALLET
  },
  {
    key: 'campaignSales',
    label: 'dashboard.campaign.sales',
    color: '#FF9800',
    enumValue: PurchaseType.CAMPAIGN
  },
  {
    key: 'cardSales',
    label: 'dashboard.card.sales',
    color: '#9C27B0',
    enumValue: PurchaseType.CARD
  },
  {
    key: 'directSales',
    label: 'dashboard.direct.sales',
    color: '#F44336',
    enumValue: PurchaseType.DIRECT
  },
  {
    key: 'physicalCardSales',
    label: 'dashboard.physical.card.sales',
    color: '#9E9E9E',
    enumValue: PurchaseType.PHYSICAL_CARD
  },
  {
    key: 'mealCardSales',
    label: 'dashboard.meal.card.sales',
    color: '#009688',
    enumValue: PurchaseType.MEAL_CARD
  },
  {
    key: 'externalPlatformSales',
    label: 'dashboard.external.platform.sales',
    color: '#FF5722',
    enumValue: PurchaseType.EXTERNAL_PLATFORM
  },
  {
    key: 'loyaltyPointsSales',
    label: 'dashboard.loyalty.points.sales',
    color: '#E91E63',
    enumValue: PurchaseType.LOYALTY_POINTS
  },
  {
    key: 'loyaltyHybridSales',
    label: 'dashboard.loyalty.hybrid.sales',
    color: '#00BCD4',
    enumValue: PurchaseType.LOYALTY_POINTS_HYBRID
  },
  {
    key: 'membershipPurchaseSales',
    label: 'dashboard.membership.purchase.sales',
    color: '#795548',
    enumValue: PurchaseType.MEMBERSHIP
  },
  {
    key: 'mixedSales',
    label: 'dashboard.mixed.sales',
    statsLabel: 'dashboard.mixed.sales.unattributed',
    color: '#607D8B',
    enumValue: PurchaseType.MIXED
  },
  // Açık Hesap: fiscal (fiş basılır) but no money collected — customer owes
  // the branch. Reported separately so it can never be mistaken for cash on
  // hand. Backend enforces the split in dashboard.service.spec.ts.
  {
    key: 'openAccountSales',
    label: 'dashboard.open.account.sales',
    color: '#5D4037',
    enumValue: PurchaseType.OPEN_ACCOUNT,
    isReceivable: true
  },
  // No enumValue: this is not a real tender — it flags rows whose purchaseType
  // was null (data-quality issue). Shown so the sum isn't silently attributed
  // elsewhere.
  {
    key: 'unknownSales',
    label: 'dashboard.unknown.sales',
    color: '#BDBDBD',
    isDataQuality: true
  }
];
